import React, { useState, useEffect, useRef } from "react";
import AdminSidebar from "../../components/AdminSidebar";
import AdminService from "../../services/admin_service";
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import {
  FiUsers, FiLayers, FiCheckCircle, FiClock,
  FiAlertCircle, FiActivity, FiGrid, FiArrowRight, FiRefreshCw,
  FiTarget, FiBriefcase, FiX, FiSearch, FiMessageSquare,
  FiZap, FiPause, FiSlash, FiChevronDown, FiChevronRight,
  FiCalendar, FiList,
} from "react-icons/fi";

const COLORS = ["#D4AF37", "#422AFB", "#10B981", "#EF4444", "#F59E0B", "#3B82F6"];

const STATUS_STYLE = {
  "Pending":          { color: "#F59E0B", bg: "#FEF3C7", border: "#FDE68A" },
  "Resolved":         { color: "#10B981", bg: "#ECFDF5", border: "#A7F3D0" },
  "Rejected":         { color: "#EF4444", bg: "#FEF2F2", border: "#FECACA" },
  "Hold":             { color: "#8B5CF6", bg: "#F5F3FF", border: "#DDD6FE" },
  "Work in Progress": { color: "#3B82F6", bg: "#EFF6FF", border: "#BFDBFE" },
  "In Queue":         { color: "#EA580C", bg: "#FFF7ED", border: "#FED7AA" },
  "Next Week":        { color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE" },
  "Next Month":       { color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE" },
  "Next Quarter":     { color: "#4338CA", bg: "#EEF2FF", border: "#C7D2FE" },
};

/* ═══════════════════════════════════════════════════════════════
   ADMIN DASHBOARD
═══════════════════════════════════════════════════════════════ */
const AdminDashboard = () => {
  const [stats,            setStats]            = useState(null);
  const [recentTickets,    setRecentTickets]    = useState([]);
  const [statusData,       setStatusData]       = useState([]);
  const [categoryWorkload, setCategoryWorkload] = useState([]);
  const [hodWorkload,      setHodWorkload]      = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [refreshing,       setRefreshing]       = useState(false);

  // Panel state
  const [panelOpen,    setPanelOpen]    = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);
  const [allTickets,   setAllTickets]   = useState([]);
  const [panelLoading, setPanelLoading] = useState(false);
  const [search,       setSearch]       = useState("");
  const panelRef = useRef(null);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
const [s, recent, status, catWL, hodWL] = await Promise.all([
  AdminService.getDashboardStats(),
  AdminService.getRecentTickets(8),
  AdminService.getTicketsByStatus(),
  AdminService.getCategoryWorkload(),
  AdminService.getHodWorkload(),
]);

console.log("FULL STATS RESPONSE => ", s);
console.log("TICKETS OBJECT => ", s.data?.tickets);
console.log("NEXT MONTH VALUE => ", s.data?.tickets?.nextMonth);

if (s.success) setStats(s.data);
      if (recent.success) setRecentTickets(recent.data);
      if (status.success) setStatusData(status.data);
      if (catWL.success)  setCategoryWorkload(catWL.data);
      if (hodWL.success)  setHodWorkload(hodWL.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  // Open panel on card click
  const handleCardClick = async (filter) => {
    setActiveFilter(filter);
    setSearch("");
    setPanelOpen(true);
    setPanelLoading(true);
    try {
      const res = await AdminService.getGlobalTickets();
      if (res.success) setAllTickets(res.data);
    } catch (e) { console.error(e); }
    finally { setPanelLoading(false); }
    setTimeout(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  const refreshPanel = async () => {
    setPanelLoading(true);
    try {
      const res = await AdminService.getGlobalTickets();
      if (res.success) setAllTickets(res.data);
    } catch (e) { console.error(e); }
    finally { setPanelLoading(false); }
  };

  const closePanel = () => { setPanelOpen(false); setActiveFilter(null); };

  // Filter by status + search
  const filtered = allTickets.filter(t => {
    const matchStatus =
      !activeFilter ||
      activeFilter === "All" ||
      (t.status || "").toLowerCase() === (activeFilter || "").toLowerCase();
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (t.ticket_code || "").toLowerCase().includes(q) ||
      (t.raised_by   || "").toLowerCase().includes(q) ||
      (t.category_name || "").toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  // Group by category
  const grouped = filtered.reduce((acc, t) => {
    const cat = t.category_name || "Uncategorized";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  const panelTitle = activeFilter === "All" ? "All Tickets" : `${activeFilter} Tickets`;
  const headerAccent = STATUS_STYLE[activeFilter] || {};

  if (loading) return <Loader />;

  const pieData = statusData.map(d => ({
    name: d.status,
    value: Number(d.count),
    pct: stats ? Math.round((d.count / stats.tickets.total) * 100) : 0,
  }));

  return (
    <div style={s.layout}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes slideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes panelIn { from{opacity:0;transform:translateY(24px) scale(.985)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes fadeIn  { from{opacity:0} to{opacity:1} }

        .scard { transition: transform .18s, box-shadow .18s !important; }
        .scard.clickable:hover { transform: translateY(-5px) !important; box-shadow: 0 14px 36px rgba(0,0,0,.09) !important; cursor:pointer; }
        .scard.is-active { outline: 3px solid #D4AF37 !important; transform: translateY(-3px) !important; }

        .hod-row:hover  { background:#F4F7FE !important; }
        .rct-row:hover  { background:#F8FAFF !important; }
        .cat-head:hover { background:#EEF2FF !important; cursor:pointer; }
        .tkt-row:hover  { background:#F8FAFF !important; }

        .refresh-btn:hover  { background:#EEF2FF !important; color:#422AFB !important; }
        .p-close:hover      { background:#FEF2F2 !important; color:#EF4444 !important; }
        .p-refresh:hover    { background:#EEF2FF !important; color:#422AFB !important; }
        .srch:focus         { border-color:#D4AF37 !important; outline:none; }
        .srch-clear:hover   { background:#E2E8F0 !important; }
      `}</style>

      <AdminSidebar />
      <div style={s.main}>

        {/* ── HEADER ── */}
        <div style={s.header}>
          <div>
            <div style={s.breadcrumb}>
              <FiGrid size={10}/>
              <span style={{color:"#D4AF37",fontWeight:700}}>Admin</span>
              <FiArrowRight size={9} style={{color:"#CBD5E1"}}/>
              <span>Dashboard</span>
            </div>
            <h1 style={s.title}>Dashboard</h1>
            <p style={s.subtitle}>Real-time system overview and analytics</p>
          </div>
          <button className="refresh-btn" style={s.refreshBtn}
            onClick={() => fetchDashboard(true)} disabled={refreshing}>
            <FiRefreshCw size={14} style={refreshing ? {animation:"spin .8s linear infinite"} : {}}/>
            {refreshing ? "Syncing..." : "Refresh"}
          </button>
        </div>

        {/* ══ CLICKABLE STATUS CARDS ══ */}
        <div style={s.topGrid}>
          <div className={`scard clickable${activeFilter==="All"?" is-active":""}`}
            style={{...s.card4, background:"#FFFBEB"}}
            onClick={() => handleCardClick("All")}>
            <div style={s.cardIcon}><FiTarget size={18} color="#D4AF37"/></div>
            <div style={{flex:1}}>
              <div style={s.cardLabel}>Total Tickets</div>
              <div style={s.cardVal}>{stats?.tickets.total || 0}</div>
              <div style={s.cardSub}>All time</div>
            </div>
            <div style={{fontSize:11,fontWeight:800,color:"#10B981"}}>+12%</div>
          </div>

          <div className={`scard clickable${activeFilter==="Pending"?" is-active":""}`}
            style={{...s.card4, background:"#FEF3C7"}}
            onClick={() => handleCardClick("Pending")}>
            <div style={s.cardIcon}><FiClock size={18} color="#F59E0B"/></div>
            <div style={{flex:1}}>
              <div style={s.cardLabel}>Pending</div>
              <div style={s.cardVal}>{stats?.tickets.pending || 0}</div>
              <div style={s.cardSub}>Awaiting resolution</div>
            </div>
            <div style={{fontSize:11,fontWeight:800,color:stats?.tickets.pending>5?"#EF4444":"#10B981"}}>
              {stats?.tickets.pending > 5 ? "⚠️ High" : "✓ Low"}
            </div>
          </div>

          <div className={`scard clickable${activeFilter==="Resolved"?" is-active":""}`}
            style={{...s.card4, background:"#ECFDF5"}}
            onClick={() => handleCardClick("Resolved")}>
            <div style={s.cardIcon}><FiCheckCircle size={18} color="#10B981"/></div>
            <div style={{flex:1}}>
              <div style={s.cardLabel}>Resolved</div>
              <div style={s.cardVal}>{stats?.tickets.resolved || 0}</div>
              <div style={s.cardSub}>Completed</div>
            </div>
            <div style={{fontSize:11,fontWeight:800,color:"#10B981"}}>+8%</div>
          </div>

          <div className="scard" style={{...s.card4, background:"#EEF2FF"}}>
            <div style={s.cardIcon}><FiUsers size={18} color="#422AFB"/></div>
            <div style={{flex:1}}>
              <div style={s.cardLabel}>Total Concern Persons</div>
              <div style={s.cardVal}>{stats?.system.hods || 0}</div>
              <div style={s.cardSub}>Managing operations</div>
            </div>
            <div style={{fontSize:11,fontWeight:800,color:"#94A3B8"}}>Steady</div>
          </div>
        </div>

        {/* ══ SECOND ROW ══ */}
        <div style={s.secondRow}>
          <div className={`scard clickable${activeFilter==="Rejected"?" is-active":""}`}
            style={{...s.miniCard, background:"#FEF2F2"}}
            onClick={() => handleCardClick("Rejected")}>
            <div style={s.miniIcon}><FiSlash size={15} color="#EF4444"/></div>
            <div>
              <div style={s.miniLabel}>Rejected</div>
              <div style={{...s.miniVal, color:"#EF4444"}}>{stats?.tickets.rejected || 0}</div>
            </div>
          </div>

          <div className={`scard clickable${activeFilter==="Hold"?" is-active":""}`}
            style={{...s.miniCard, background:"#F5F3FF"}}
            onClick={() => handleCardClick("Hold")}>
            <div style={s.miniIcon}><FiPause size={15} color="#8B5CF6"/></div>
            <div>
              <div style={s.miniLabel}>On Hold</div>
              <div style={{...s.miniVal, color:"#8B5CF6"}}>{stats?.tickets.hold || 0}</div>
            </div>
          </div>

          <div className={`scard clickable${activeFilter==="Work in Progress"?" is-active":""}`}
            style={{...s.miniCard, background:"#EFF6FF"}}
            onClick={() => handleCardClick("Work in Progress")}>
            <div style={s.miniIcon}><FiZap size={15} color="#3B82F6"/></div>
            <div>
              <div style={s.miniLabel}>Work in Progress</div>
              <div style={{...s.miniVal, color:"#3B82F6"}}>{stats?.tickets.inProgress || 0}</div>
            </div>
          </div>

          <div className={`scard clickable${activeFilter==="In Queue"?" is-active":""}`}
            style={{...s.miniCard, background:"#FFF7ED"}}
            onClick={() => handleCardClick("In Queue")}>
            <div style={s.miniIcon}><FiList size={15} color="#EA580C"/></div>
            <div>
              <div style={s.miniLabel}>In Queue</div>
              <div style={{...s.miniVal, color:"#EA580C"}}>{stats?.tickets.inQueue || 0}</div>
            </div>
          </div>

          <div className={`scard clickable${activeFilter==="Next Week"?" is-active":""}`}
            style={{...s.miniCard, background:"#EFF6FF"}}
            onClick={() => handleCardClick("Next Week")}>
            <div style={s.miniIcon}><FiCalendar size={15} color="#2563EB"/></div>
            <div>
              <div style={s.miniLabel}>Next Week</div>
              <div style={{...s.miniVal, color:"#2563EB"}}>{stats?.tickets.nextWeek || 0}</div>
            </div>
          </div>

          <div className={`scard clickable${activeFilter==="Next Month"?" is-active":""}`}
            style={{...s.miniCard, background:"#F5F3FF"}}
            onClick={() => handleCardClick("Next Month")}>
            <div style={s.miniIcon}><FiCalendar size={15} color="#7C3AED"/></div>
            <div>
              <div style={s.miniLabel}>Next Month</div>
              <div style={{...s.miniVal, color:"#7C3AED"}}>{stats?.tickets.nextMonth || 0}</div>
            </div>
          </div>

          <div className={`scard clickable${activeFilter==="Next Quarter"?" is-active":""}`}
            style={{...s.miniCard, background:"#EEF2FF"}}
            onClick={() => handleCardClick("Next Quarter")}>
            <div style={s.miniIcon}><FiCalendar size={15} color="#4338CA"/></div>
            <div>
              <div style={s.miniLabel}>Next Quarter</div>
              <div style={{...s.miniVal, color:"#4338CA"}}>{stats?.tickets.nextQuarter || 0}</div>
            </div>
          </div>

          {[
            { label:"Categories", val: stats?.system.categories || 0 },
            { label:"Workers",    val: stats?.system.workers    || 0 },
            { label:"Users",      val: stats?.system.users      || 0 },
          ].map(item => (
            <div key={item.label} style={s.sysMini}>
              <div style={{fontSize:20,fontWeight:900,color:"#1B2559"}}>{item.val}</div>
              <div style={{fontSize:10,color:"#A3AED0",fontWeight:700,textTransform:"uppercase",letterSpacing:.4,marginTop:4}}>{item.label}</div>
            </div>
          ))}
        </div>

        {/* ══ CHARTS ══ */}
        <div style={s.chartGrid}>
          <div style={s.chartCard}>
            <div style={s.chartTitle}><FiActivity size={14}/> Ticket Status Distribution</div>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" labelLine={false}
                  label={({name,pct}) => `${name}: ${pct}%`}
                  outerRadius={80} dataKey="value">
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
                </Pie>
                <Tooltip formatter={v => `${v} tickets`}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div style={s.chartCard}>
            <div style={s.chartTitle}><FiLayers size={14}/> Category Workload</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={categoryWorkload}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F2FA"/>
                <XAxis dataKey="name" fontSize={11} stroke="#94A3B8"/>
                <YAxis fontSize={11} stroke="#94A3B8"/>
                <Tooltip contentStyle={{background:"#FFF",border:"1px solid #F0F2FA",borderRadius:10}}/>
                <Legend/>
                <Bar dataKey="ticket_count" fill="#1B2559" name="Total" radius={[6,6,0,0]}/>
                <Bar dataKey="resolved" fill="#10B981" name="Resolved" radius={[6,6,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ══ HOD + RECENT ══ */}
        <div style={s.bottomGrid}>
          <div style={s.infoCard}>
            <div style={s.infoTitle}><FiBriefcase size={14}/> Concern Person Performance</div>
            {hodWorkload.slice(0,6).map(h => (
              <div key={h.id} className="hod-row" style={s.hodRow}>
                <span style={{fontSize:13,fontWeight:700,color:"#1B2559"}}>{h.full_name || "Unknown"}</span>
                <div style={{display:"flex",gap:12}}>
                  <span style={s.hodStat}><FiTarget size={11}/> {h.assigned_tickets || 0}</span>
                  <span style={s.hodStat}><FiCheckCircle size={11} color="#10B981"/> {h.resolved || 0}</span>
                </div>
              </div>
            ))}
          </div>
          <div style={s.infoCard}>
            <div style={s.infoTitle}><FiClock size={14}/> Recent Activity</div>
            {recentTickets.slice(0,6).map(t => {
              const st2 = STATUS_STYLE[t.status] || {color:"#94A3B8",bg:"#F4F7FE"};
              return (
                <div key={t.id} className="rct-row" style={s.rctRow}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:800,color:"#1B2559",fontFamily:"monospace"}}>{t.ticket_code}</div>
                    <div style={{fontSize:11,color:"#94A3B8",marginTop:2}}>{(t.description||"").substring(0,40)}...</div>
                  </div>
                  <div style={{padding:"3px 8px",borderRadius:8,fontSize:10,fontWeight:700,background:st2.bg,color:st2.color}}>{t.status}</div>
                  <div style={{fontSize:10,color:"#CBD5E1",fontWeight:600,minWidth:46}}>
                    {new Date(t.created_at).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════
            TICKET DETAIL PANEL
        ═══════════════════════════════════════════════════════════ */}
        {panelOpen && (
          <div ref={panelRef} style={{marginTop:12, marginBottom:32, animation:"fadeIn .25s ease"}}>
            <div style={s.panel}>
              <div style={s.panelHeader}>
                <div style={{display:"flex", alignItems:"center", gap:14}}>
                  <div style={{
                    width:13, height:13, borderRadius:"50%", flexShrink:0,
                    background: headerAccent.color || "#D4AF37",
                    boxShadow: `0 0 0 3px rgba(255,255,255,0.2)`,
                  }}/>
                  <div>
                    <h2 style={{margin:0, fontSize:18, fontWeight:900, color:"#FFF"}}>{panelTitle}</h2>
                    <p style={{margin:"3px 0 0", fontSize:11, color:"rgba(255,255,255,.45)", fontWeight:600}}>
                      {panelLoading ? "Loading..." : `${filtered.length} ticket${filtered.length!==1?"s":""} found`}
                    </p>
                  </div>
                </div>
                <div style={{display:"flex", gap:10}}>
                  <button className="p-refresh" style={s.pBtn} onClick={refreshPanel} disabled={panelLoading}>
                    <FiRefreshCw size={13} style={panelLoading?{animation:"spin .8s linear infinite"}:{}}/>
                    Refresh
                  </button>
                  <button className="p-close" style={s.pClose} onClick={closePanel}>
                    <FiX size={15}/> Close
                  </button>
                </div>
              </div>

              <div style={s.panelSearch}>
                <FiSearch size={13} style={{color:"#A3AED0",flexShrink:0}}/>
                <input
                  className="srch"
                  style={s.srchInput}
                  placeholder="Search ticket code, employee name, category..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && (
                  <button className="srch-clear" style={s.srchClear} onClick={() => setSearch("")}>
                    <FiX size={11}/>
                  </button>
                )}
              </div>

              <div style={s.panelBody}>
                {panelLoading ? (
                  <div style={s.panelCenter}>
                    <div style={s.smallSpin}/>
                    <span style={{color:"#A3AED0",fontSize:13,fontWeight:600}}>Fetching tickets...</span>
                  </div>
                ) : filtered.length === 0 ? (
                  <div style={s.panelCenter}>
                    <FiAlertCircle size={30} style={{color:"#CBD5E1",marginBottom:10}}/>
                    <div style={{fontSize:14,fontWeight:700,color:"#94A3B8"}}>No tickets found</div>
                    <div style={{fontSize:12,color:"#CBD5E1",marginTop:4}}>
                      {search
                        ? "Try clearing your search"
                        : `No ${activeFilter==="All" ? "" : activeFilter+" "}tickets available`}
                    </div>
                  </div>
                ) : (
                  Object.entries(grouped).map(([cat, tickets]) => (
                    <CategoryBlock key={cat} category={cat} tickets={tickets}/>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

/* ─── CATEGORY BLOCK ──────────────────────────────────────────────────────── */
const CategoryBlock = ({ category, tickets }) => {
  const [open, setOpen] = useState(true);
  return (
    <div style={cb.wrap}>
      <div className="cat-head" style={cb.head} onClick={() => setOpen(o => !o)}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={cb.dot}/>
          <span style={cb.catName}>{category}</span>
          <span style={cb.count}>{tickets.length} ticket{tickets.length!==1?"s":""}</span>
        </div>
        {open
          ? <FiChevronDown size={14} style={{color:"#94A3B8"}}/>
          : <FiChevronRight size={14} style={{color:"#94A3B8"}}/>}
      </div>

      {open && (
        <div>
          <div style={cb.thead}>
            <span style={{...cb.th, flex:"1.3"}}>Ticket Code</span>
            <span style={{...cb.th, flex:"1.8"}}>Employee</span>
            <span style={{...cb.th, flex:"1.5"}}>Subcategory</span>
            <span style={{...cb.th, flex:"1"}}>Status</span>
            <span style={{...cb.th, flex:"1"}}>Date</span>
            <span style={{...cb.th, flex:"1.2"}}>Worker</span>
            <span style={{...cb.th, flex:"0.8"}}>Rating</span>
            <span style={{...cb.th, flex:"2"}}>Feedback</span>
          </div>
          {tickets.map((t, i) => <TicketRow key={t.id || i} ticket={t}/>)}
        </div>
      )}
    </div>
  );
};

/* ─── TICKET ROW ─────────────────────────────────────────────────────────── */
const TicketRow = ({ ticket: t }) => {
  const st = STATUS_STYLE[t.status] || {color:"#94A3B8",bg:"#F4F7FE",border:"#E9EDF7"};
  const stars = Math.max(0, Math.min(5, Math.round(Number(t.rating) || 0)));
  return (
    <div className="tkt-row" style={cb.row}>
      <div style={{...cb.cell, flex:"1.3"}}>
        <span style={cb.code}>{t.ticket_code || "—"}</span>
      </div>
      <div style={{...cb.cell, flex:"1.8"}}>
        <div style={cb.avatar}>{(t.raised_by||"?").charAt(0).toUpperCase()}</div>
        <span style={cb.empName}>{t.raised_by || "Unknown"}</span>
      </div>
      <div style={{...cb.cell, flex:"1.5"}}>
        <span style={{fontSize:11,color:"#64748B",fontWeight:600}}>{t.sub_category_name || "—"}</span>
      </div>
      <div style={{...cb.cell, flex:"1"}}>
        <span style={{
          padding:"3px 9px", borderRadius:20, fontSize:10, fontWeight:700,
          color:st.color, background:st.bg, border:`1px solid ${st.border}`, whiteSpace:"nowrap",
        }}>{t.status || "—"}</span>
      </div>
      <div style={{...cb.cell, flex:"1"}}>
        <span style={{fontSize:11,color:"#94A3B8",fontWeight:600}}>
          {t.created_at
            ? new Date(t.created_at).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"2-digit"})
            : "—"}
        </span>
      </div>
      <div style={{...cb.cell, flex:"1.2"}}>
        <span style={{fontSize:11,fontWeight:600,color:"#1B2559"}}>
          {t.assigned_worker_name || <span style={{color:"#CBD5E1"}}>Unassigned</span>}
        </span>
      </div>
      <div style={{...cb.cell, flex:"0.8"}}>
        {stars > 0
          ? <span style={{color:"#D4AF37",fontSize:12,letterSpacing:1}}>
              {"★".repeat(stars)}{"☆".repeat(5-stars)}
            </span>
          : <span style={{fontSize:10,color:"#CBD5E1"}}>—</span>}
      </div>
      <div style={{...cb.cell, flex:"2", minWidth:0, overflow:"hidden"}}>
        {t.feedback
          ? <span style={{display:"flex",gap:4,alignItems:"flex-start",fontSize:10,color:"#64748B",fontStyle:"italic",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
              <FiMessageSquare size={9} style={{color:"#A3AED0",flexShrink:0,marginTop:1}}/>
              {t.feedback}
            </span>
          : <span style={{fontSize:10,color:"#CBD5E1"}}>—</span>}
      </div>
    </div>
  );
};

/* ─── LOADER ─────────────────────────────────────────────────────────────── */
const Loader = () => (
  <div style={{height:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#F4F7FE",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:12}}>
      <div style={{width:56,height:56,border:"3px solid #E9EDF7",borderTopColor:"#D4AF37",borderRadius:"50%",animation:"spin 1s linear infinite"}}/>
      <div style={{fontSize:14,fontWeight:900,color:"#1B2559",letterSpacing:6}}>ACHAL JEWELS</div>
      <div style={{fontSize:12,color:"#A3AED0",fontWeight:600}}>Loading Dashboard...</div>
    </div>
  </div>
);

/* ─── CATEGORY BLOCK STYLES ─────────────────────────────────────────────── */
const cb = {
  wrap:    { marginBottom:18, borderRadius:14, overflow:"hidden", border:"1px solid #F0F2FA" },
  head:    { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 18px", background:"#F8FAFF", borderBottom:"1px solid #F0F2FA", transition:"background .15s" },
  dot:     { width:8, height:8, borderRadius:"50%", background:"#D4AF37", flexShrink:0 },
  catName: { fontSize:13, fontWeight:800, color:"#1B2559" },
  count:   { fontSize:11, fontWeight:700, color:"#A3AED0", background:"#E9EDF7", padding:"2px 9px", borderRadius:20 },
  thead:   { display:"flex", gap:8, padding:"8px 16px", background:"#F4F7FE", borderBottom:"1px solid #F0F2FA" },
  th:      { fontSize:9, fontWeight:800, color:"#A3AED0", textTransform:"uppercase", letterSpacing:.5 },
  row:     { display:"flex", alignItems:"center", gap:8, padding:"11px 16px", borderBottom:"1px solid #F8FAFC", background:"#FFF", transition:"background .12s" },
  cell:    { display:"flex", alignItems:"center", gap:6, minWidth:0 },
  code:    { fontFamily:"monospace", fontSize:11, fontWeight:800, color:"#1B2559", background:"#EEF2FF", padding:"3px 7px", borderRadius:6, whiteSpace:"nowrap" },
  avatar:  { width:26, height:26, borderRadius:7, background:"linear-gradient(135deg,#1B2559,#422AFB)", color:"#D4AF37", fontSize:11, fontWeight:900, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  empName: { fontSize:12, fontWeight:700, color:"#1B2559", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
};

/* ─── MAIN STYLES ────────────────────────────────────────────────────────── */
const s = {
  layout: { display:"flex", background:"#F4F7FE", minHeight:"100vh", fontFamily:"'Plus Jakarta Sans',sans-serif" },
  main:   { marginLeft:280, padding:"32px 36px", flex:1, minWidth:0 },

  header:     { display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:28 },
  breadcrumb: { display:"flex", alignItems:"center", gap:6, fontSize:11, color:"#A3AED0", fontWeight:600, marginBottom:6 },
  title:      { margin:0, fontSize:28, fontWeight:900, color:"#1B2559", letterSpacing:"-0.5px" },
  subtitle:   { margin:"4px 0 0", fontSize:13, color:"#A3AED0", fontWeight:500 },
  refreshBtn: { padding:"10px 18px", borderRadius:12, border:"1px solid #E9EDF7", background:"#F4F7FE", color:"#A3AED0", fontWeight:700, fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", gap:6, transition:"0.2s" },

  topGrid:   { display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:14, animation:"slideUp .4s ease" },
  card4:     { borderRadius:16, padding:"20px", border:"1px solid #F0F2FA", display:"flex", alignItems:"center", gap:14, boxShadow:"0 2px 8px rgba(0,0,0,0.03)" },
  cardIcon:  { width:44, height:44, borderRadius:12, background:"rgba(255,255,255,.5)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  cardLabel: { fontSize:11, color:"#A3AED0", fontWeight:700, textTransform:"uppercase", letterSpacing:.5 },
  cardVal:   { fontSize:26, fontWeight:900, color:"#1B2559", marginTop:2 },
  cardSub:   { fontSize:11, color:"#94A3B8", fontWeight:500, marginTop:2 },

  // ── FIXED: auto-fit wrapping, no horizontal scroll
  secondRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: 12,
    marginBottom: 20,
    width: "100%",
    alignItems: "stretch",
  },
  miniCard:  { borderRadius:14, padding:"14px 16px", border:"1px solid #F0F2FA", display:"flex", alignItems:"center", gap:10, boxShadow:"0 2px 6px rgba(0,0,0,.02)" },
  miniIcon:  { width:34, height:34, borderRadius:10, background:"rgba(255,255,255,.5)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  miniLabel: { fontSize:10, color:"#A3AED0", fontWeight:700, textTransform:"uppercase", letterSpacing:.3, lineHeight:1.2 },
  miniVal:   { fontSize:20, fontWeight:900, marginTop:2 },
  sysMini:   { borderRadius:14, padding:"14px 16px", border:"1px solid #F0F2FA", background:"#FFF", textAlign:"center", boxShadow:"0 2px 6px rgba(0,0,0,.02)" },

  chartGrid: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20 },
  chartCard: { background:"#FFF", borderRadius:18, padding:"22px", border:"1px solid #F0F2FA", boxShadow:"0 2px 12px rgba(0,0,0,.03)" },
  chartTitle:{ display:"flex", alignItems:"center", gap:8, fontSize:14, fontWeight:800, color:"#1B2559", marginBottom:18 },

  bottomGrid: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:8 },
  infoCard:   { background:"#FFF", borderRadius:18, padding:"22px", border:"1px solid #F0F2FA", boxShadow:"0 2px 12px rgba(0,0,0,.03)" },
  infoTitle:  { display:"flex", alignItems:"center", gap:8, fontSize:14, fontWeight:800, color:"#1B2559", marginBottom:16, paddingBottom:12, borderBottom:"1px solid #F4F7FE" },
  hodRow:     { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 12px", borderRadius:10, background:"#FAFBFF", marginBottom:8, transition:"background .15s" },
  hodStat:    { display:"flex", alignItems:"center", gap:4, fontSize:12, color:"#94A3B8", fontWeight:600 },
  rctRow:     { display:"flex", alignItems:"center", gap:12, padding:"10px 12px", borderRadius:10, background:"#FAFBFF", borderLeft:"3px solid #D4AF37", marginBottom:8, transition:"background .15s" },

  panel: {
    background:"#FFF", borderRadius:20,
    border:"1px solid #E9EDF7",
    boxShadow:"0 16px 48px rgba(27,37,89,.1), 0 4px 16px rgba(0,0,0,.05)",
    overflow:"hidden",
    animation:"panelIn .35s cubic-bezier(.16,1,.3,1) both",
  },
  panelHeader: {
    display:"flex", justifyContent:"space-between", alignItems:"center",
    padding:"18px 24px", background:"#1B2559",
    position:"sticky", top:0, zIndex:5,
  },
  pBtn: {
    display:"flex", alignItems:"center", gap:6,
    padding:"8px 14px", borderRadius:10,
    border:"1px solid rgba(255,255,255,.2)", background:"rgba(255,255,255,.08)",
    color:"rgba(255,255,255,.65)", fontWeight:700, fontSize:12, cursor:"pointer", transition:".15s",
  },
  pClose: {
    display:"flex", alignItems:"center", gap:6,
    padding:"8px 14px", borderRadius:10,
    border:"1px solid rgba(239,68,68,.4)", background:"rgba(239,68,68,.08)",
    color:"#FCA5A5", fontWeight:700, fontSize:12, cursor:"pointer", transition:".15s",
  },
  panelSearch: {
    display:"flex", alignItems:"center", gap:10,
    padding:"14px 24px", background:"#F8FAFF",
    borderBottom:"1px solid #F0F2FA",
  },
  srchInput: {
    border:"1px solid #E9EDF7", borderRadius:10, padding:"9px 14px",
    fontSize:13, color:"#1B2559", fontWeight:500, flex:1,
    fontFamily:"'Plus Jakarta Sans',sans-serif", transition:"border-color .2s", outline:"none",
    background:"#FFF",
  },
  srchClear:  { background:"#F1F5F9", border:"none", borderRadius:"50%", width:22, height:22, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#64748B", transition:".15s" },
  panelBody:  { padding:"20px 24px", maxHeight:560, overflowY:"auto" },
  panelCenter:{ display:"flex", flexDirection:"column", alignItems:"center", padding:"60px 0", gap:10 },
  smallSpin:  { width:26, height:26, border:"2px solid #E9EDF7", borderTopColor:"#D4AF37", borderRadius:"50%", animation:"spin .8s linear infinite" },
};

export default AdminDashboard;